import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { last, get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getBaseBreadcrumb, getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { ShareThisChart } from 'client/site-modules/shared/components/share-this-chart/share-this-chart';
import { USMap } from 'client/site-modules/shared/components/us-map/us-map';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { StateCard } from 'site-modules/shared/components/infographics/state-card/state-card';
import { PoweredByEdmunds } from 'site-modules/shared/components/powered-by-edmunds/powered-by-edmunds';
import { TopColors } from 'client/site-modules/infographics/components/top-colors/top-colors';
import { MPC_CONTENT_PATH, NAMES_TO_COLOR_CODES } from 'client/site-modules/infographics/constants/most-popular-colors';
import { StateCardColorsContent } from 'client/site-modules/infographics/components/state-card-colors-content/state-card-colors-content';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { ArticleTable } from 'site-modules/shared/components/editorial/article-table/article-table';

import './most-popular-colors.scss';

const PAGE_URL = 'https://www.edmunds.com/most-popular-car-colors/';

export function getStatesColorMap(statesData) {
  const colorMap = {};
  statesData.forEach(state => {
    const { mostPopularColor1, stateCode } = state;
    const color = mostPopularColor1.toLowerCase();
    const colorCode = NAMES_TO_COLOR_CODES[color] || NAMES_TO_COLOR_CODES.default;
    if (!colorMap[colorCode]) {
      colorMap[colorCode] = [stateCode];
    } else {
      colorMap[colorCode].push(stateCode);
    }
  });
  return colorMap;
}

export class MostPopularColorsUI extends Component {
  static propTypes = {
    content: CmsEntities.Content,
    isWidget: PropTypes.bool,
    isMobile: PropTypes.bool,
  };

  static defaultProps = {
    content: DEFAULT_CONTENT,
    isWidget: false,
    isMobile: false,
  };

  state = {
    isStateCardOpened: false,
    selectedState: null,
  };

  onStateChange = state => {
    this.openStateCard();
    this.setSelectedState(state.stateCode);
  };

  onMapStateClick = clickedStateId => {
    this.openStateCard();
    this.setSelectedState(clickedStateId);
  };

  setSelectedState = stateId => {
    this.setState({ selectedState: stateId, selectedVehicle: null });
  };

  openStateCard = () => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    this.setState({ isStateCardOpened: true });
  };

  closeStateCard = () => this.setState({ isStateCardOpened: false, selectedState: null });

  render() {
    const { content, isWidget, isMobile } = this.props;
    const { selectedState, isStateCardOpened } = this.state;
    const { title } = content;
    const breadcrumbs = [getHomeBreadcrumb(), getBaseBreadcrumb(title)];
    const stateOptions = content
      .child('state-vehicle-color')
      .children()
      .map(state => ({ ...state.getAllMetadata(), stateCode: last(state.id.split('-')) }));

    const selectedStateData = stateOptions.find(state => state.stateCode === selectedState);

    const shareableWidgetData = {
      widgetName: 'most-popular-car-colors',
      defaultWidth: 425,
      defaultHeight: 720,
      minWidth: 290,
      minHeight: 600,
      defaultTitle: title,
    };

    let statesColorMap = getStatesColorMap(stateOptions);
    statesColorMap = {
      ...statesColorMap,
      [NAMES_TO_COLOR_CODES.default]: [selectedState],
    };

    const selectedStateName = get(selectedStateData, 'state', '');

    return (
      <main id="main-content" tabIndex={-1} className="most-popular-colors pb-3">
        <Container>
          {!isWidget && <Breadcrumbs className="ps-0 mb-2" data={breadcrumbs} />}
          <div className="d-flex justify-content-between mb-1">
            <h1 className="heading-2 mb-0">{title}</h1>
            {!isMobile && (
              <ShareThisChart
                pageTitle={title}
                pageUrl={PAGE_URL}
                shareableWidgetData={shareableWidgetData}
                className="size-16"
              />
            )}
          </div>
          {isMobile && (
            <ShareThisChart
              pageTitle={title}
              pageUrl={PAGE_URL}
              shareableWidgetData={shareableWidgetData}
              className="size-16"
            />
          )}
          {!isWidget && (
            <Row className="mt-1 mb-1_5">
              <Col xs={12} xl={9}>
                <ContentFragment componentToUse="div" classes="page-content">
                  {content.child('html').content}
                </ContentFragment>
              </Col>
            </Row>
          )}
          <p className={classnames('fw-bold text-gray-darker mt-1_5', isMobile ? 'size-16' : 'size-20')}>
            Top 5 colors in the U.S.
          </p>
          <TopColors content={content.child('colors-mapping')} isMobile={isMobile} />
          {isMobile && (
            <Row className="justify-content-center mt-3">
              <Col xs={9}>
                <StyledSelect
                  toggle="Select a state"
                  options={stateOptions}
                  onChange={this.onStateChange}
                  value={selectedState}
                  name="select-state"
                  valueKey="stateCode"
                  labelKey="state"
                  className="text-gray-darker"
                />
              </Col>
            </Row>
          )}
          <Row className="mt-2 mb-1_5">
            <Col xs={12} className="px-md-3">
              <USMap
                statesColorMap={statesColorMap}
                isClickable
                onMapStateClick={this.onMapStateClick}
                isMobile={isMobile}
                strokeColor="#007EE5"
                defaultColor={NAMES_TO_COLOR_CODES.default}
                labelsColor="#555555"
              />
              <StateCard
                cardTitle={selectedStateName}
                pageTitle={title}
                pageUrl={PAGE_URL}
                isOpen={!!(isStateCardOpened && selectedStateData)}
                onClose={this.closeStateCard}
                isAbsolutelyPositioned={!isMobile}
                isModal={isMobile}
                closeOnClickOutside
              >
                <StateCardColorsContent stateData={selectedStateData} />
              </StateCard>
            </Col>
          </Row>
          <PoweredByEdmunds className="mt-1" />
        </Container>
        {!isWidget && (
          <Fragment>
            <Container className="mt-2 mb-3">
              <hr className="mt-1_5 mb-2" />
              <ArticleTable entry={content.child('nationwide-popular-colors-table')} />
              <ContentFragment componentToUse="div" classes="footnote">
                {content.child('html-1').content}
              </ContentFragment>
            </Container>
          </Fragment>
        )}
      </main>
    );
  }
}

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const stateToPropsConfig = {
  content: bindToContent(MPC_CONTENT_PATH),
};

export const MostPopularColors = connect(mapStateToProps)(connectToModel(MostPopularColorsUI, stateToPropsConfig));
